import {cn} from '@sollapay/ui';
import {Button, Divider, PopoverContent, PopoverTrigger, Typography} from '@sollapay/ui/components';
import {useId} from 'react';

import type {FC, ReactNode} from 'react';

export interface RosterPopoverContentProps {
  title: ReactNode;
  count: number;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * The shell for every "who is on this" dropdown (team, sellers, buyers): a title with the count at
 * its end, a list capped at 252px that scrolls, and an optional footer for a link.
 */
export const RosterPopoverContent: FC<RosterPopoverContentProps> = ({
  title,
  count,
  children,
  footer
}) => {
  const titleId = useId();

  return (
    <PopoverContent
      align="start"
      aria-labelledby={titleId}
      className="w-[290px] gap-0 p-0"
      // The popover is portaled but its events still bubble through the React tree — keep a clickable row or cell from opening.
      onClick={event => event.stopPropagation()}
    >
      <div
        data-slot="title"
        className="mx-4 mt-3 flex items-center justify-between gap-2 border-b border-border pb-2"
      >
        <Typography id={titleId} size="sm" color="tertiary">
          {title}
        </Typography>
        <Typography size="sm" color="tertiary" className="shrink-0">
          {count}
        </Typography>
      </div>
      <div data-slot="roster-list" className="max-h-[252px] overflow-y-auto p-2">
        {children}
      </div>
      {footer && (
        <div data-slot="footer" className="border-t border-border p-1">
          {footer}
        </div>
      )}
    </PopoverContent>
  );
};

export interface RosterPopoverTriggerProps {
  ariaLabel?: string;
  className?: string;
  overRowLink?: boolean;
  children: ReactNode;
}

const HOVER_DELAY_MS = 100;

/**
 * Opens its `RosterPopoverContent` on hover as well as on click (touch) and keyboard. With
 * `overRowLink` it is lifted above a clickable row or card (and its stretched link overlay), and
 * its click never reaches them.
 */
export const RosterPopoverTrigger: FC<RosterPopoverTriggerProps> = ({
  ariaLabel,
  className,
  overRowLink = false,
  children
}) => (
  <PopoverTrigger
    openOnHover
    delay={HOVER_DELAY_MS}
    closeDelay={HOVER_DELAY_MS}
    render={
      <Button
        variant="tertiary"
        aria-label={ariaLabel}
        className={cn(
          'h-auto cursor-pointer p-0 hover:bg-transparent',
          overRowLink && 'relative z-10',
          className
        )}
        onClick={overRowLink ? event => event.stopPropagation() : undefined}
      />
    }
  >
    {children}
  </PopoverTrigger>
);

export interface RosterPopoverRowProps {
  avatar: ReactNode;
  name: ReactNode;
  caption?: ReactNode;
  /** A badge at the row's end, e.g. "Trustee". */
  tag?: ReactNode;
  /** Makes the whole row a button. */
  onClick?: () => void;
}

export const RosterPopoverRow: FC<RosterPopoverRowProps> = ({
  avatar,
  name,
  caption,
  tag,
  onClick
}) => {
  const body = (
    <>
      {avatar}
      <span className="flex min-w-0 flex-1 flex-col text-start">
        <Typography size="sm" weight="semibold">
          {name}
        </Typography>
        {caption && (
          <Typography size="xs" color="tertiary" className="select-text">
            {caption}
          </Typography>
        )}
      </span>
      {tag}
    </>
  );

  if (!onClick) {
    return (
      <div data-slot="roster-row" className="flex items-center gap-3 rounded-sm p-2">
        {body}
      </div>
    );
  }

  return (
    <Button
      variant="tertiary"
      data-slot="roster-row"
      className="h-auto w-full justify-start gap-3 rounded-sm p-2 font-normal"
      onClick={onClick}
    >
      {body}
    </Button>
  );
};

/** Separates groups of rows, e.g. the trustee from the team. */
export const RosterPopoverDivider: FC = () => (
  <Divider className="mx-2 my-1 data-horizontal:w-auto" />
);
